import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { putAuth } from '@/api/ApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import type { FundLookupDtoIn, NedarimAutoClassifiedDtoIn, NedarimAutoClassifiedPageDtoIn } from '@/models/in/NedarimDecodeModels';
import type { NedarimTransactionFundAllocationDtoOut } from '@/models/out/NedarimTransactionFundAllocationDtoOut';
import Alert, { type AlertType } from '@/components/ui/Alert';
import Button from '@/components/ui/Button';
import FundPicker from './FundPicker';
import TablePager, { PAGE_SIZE } from './TablePager';
import { ALLOCATE_FUNDS_URL, AUTO_CLASSIFIED_URL, formatTransactionTime } from './nedarimTransactionsFundAllocation';
import styles from './fundAllocationTabs.module.scss';

// "Decoded" (matched the decode table) reads as the teal default; everything else (number rules,
// groupe/comments derivations) gets the green variant.
const methodPillClass = (method: string) => (method === 'Decoded' ? styles.methodPill : `${styles.methodPill} ${styles.methodRule}`);

const AutoClassifiedTab = ({ funds }: { funds: FundLookupDtoIn[] }) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const headers = { Authorization: `Bearer ${currentUser?.token}` };

  const { data, isLoading, isError, refetch } = useGetAuthApi<NedarimAutoClassifiedPageDtoIn>(AUTO_CLASSIFIED_URL);
  const [message, setMessage] = useState<{ type: AlertType; text: string } | null>(null);
  const [correctingId, setCorrectingId] = useState<number | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [page, setPage] = useState(0);

  const methodLabel = (method: string) => t(`nedarimTransactionsFundAllocation.tabs.method.${method}`, { defaultValue: method });

  // The traceable placements get a "what exactly matched" line; the rest stand on the pill alone.
  const methodDetail = (row: NedarimAutoClassifiedDtoIn): string | null => {
    if (row.classificationMethod === 'MosadDefault')
      return t('nedarimTransactionsFundAllocation.tabs.methodDetail.mosadDefault');
    if (!row.matchedValue || !row.matchedField) return null;
    if (row.classificationMethod === 'Decoded')
      return t('nedarimTransactionsFundAllocation.tabs.methodDetail.decoded', { value: row.matchedValue, field: row.matchedField });
    if (row.classificationMethod === 'Rule')
      return t('nedarimTransactionsFundAllocation.tabs.methodDetail.rule', { value: row.matchedValue, field: row.matchedField });
    return null;
  };

  const correct = async (row: NedarimAutoClassifiedDtoIn, fund: FundLookupDtoIn) => {
    setBusyId(row.id);
    setMessage(null);
    try {
      const body: NedarimTransactionFundAllocationDtoOut[] = [{ transactionId: String(row.id), fundId: fund.id }];
      await putAuth<unknown, object, NedarimTransactionFundAllocationDtoOut[]>(ALLOCATE_FUNDS_URL, headers, {}, body);
      setMessage({ type: 'success', text: t('nedarimTransactionsFundAllocation.tabs.corrected', { donor: row.clientName, fundId: fund.id, fundName: fund.name }) });
      setCorrectingId(null);
      await refetch();
    } catch {
      setMessage({ type: 'error', text: t('nedarimTransactionsFundAllocation.tabs.assignFailed') });
    } finally {
      setBusyId(null);
    }
  };

  if (isLoading) return <div className={styles.loading}>{t('loading')}</div>;

  // A failed request must not read as "nothing here yet": an empty rules table looked exactly like a
  // working screen with no rules while the server was in fact answering with an error.
  if (isError) return <div className={styles.loadError}>{t('nedarimTransactionsFundAllocation.tabs.loadError')}</div>;

  // A stale bundle against a redeployed server hard-crashed here when the response shape changed
  // (an open tab kept the old code across a deploy). Tolerate both shapes instead of trusting one.
  const rows: NedarimAutoClassifiedDtoIn[] = Array.isArray(data) ? data : data?.transactions ?? [];
  const totalCount = (!Array.isArray(data) && data?.totalCount) || rows.length;

  // A correction removes its row, so the current page may fall off the end — clamp, don't reset.
  const pageCount = Math.ceil(rows.length / PAGE_SIZE);
  const safePage = Math.min(page, Math.max(pageCount - 1, 0));
  const pageRows = rows.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  return (
    <div className={styles.tabPanel}>
      <p className={`body-md-regular ${styles.intro}`}>{t('nedarimTransactionsFundAllocation.tabs.autoIntro')}</p>
      <span className={`body-sm-regular ${styles.count}`}>
        {totalCount > rows.length
          ? t('nedarimTransactionsFundAllocation.tabs.autoCountShowing', { total: totalCount, shown: rows.length })
          : t('nedarimTransactionsFundAllocation.tabs.autoCount', { count: totalCount })}
      </span>

      {message && <Alert type={message.type} message={message.text} />}

      {rows.length === 0 ? (
        <div className={styles.tableCard}>
          <div className={styles.emptyState}>
            <p className="body-md-semibold">{t('nedarimTransactionsFundAllocation.tabs.autoEmpty')}</p>
          </div>
        </div>
      ) : (
        <div className={styles.tableCard}>
          <div className={styles.scroll}>
            <table>
              <thead>
                <tr>
                  <th>{t('nedarimTransactionsFundAllocation.columns.clientName')}</th>
                  <th>{t('nedarimTransactionsFundAllocation.columns.groupe')}</th>
                  <th>{t('nedarimTransactionsFundAllocation.columns.comments')}</th>
                  <th>{t('nedarimTransactionsFundAllocation.columns.amount')}</th>
                  <th>{t('nedarimTransactionsFundAllocation.columns.transactionTime')}</th>
                  <th>{t('nedarimTransactionsFundAllocation.tabs.assignedFund')}</th>
                  <th className={styles.assignCol}></th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row) => (
                  <tr key={row.id}>
                    <td className={`body-sm-regular ${styles.donor}`}>{row.clientName || '—'}</td>
                    <td className="body-sm-regular">
                      <span className={styles.src}>{row.groupe || <span className={styles.muted}>—</span>}</span>
                    </td>
                    <td className="body-sm-regular">
                      <span className={styles.src}>{row.comments || <span className={styles.muted}>—</span>}</span>
                    </td>
                    <td className={`body-sm-regular ${styles.amount}`}>{row.amount}</td>
                    <td className={`body-sm-regular ${styles.date}`}>{formatTransactionTime(row.transactionTime)}</td>
                    {/* The "why" lives with the fund itself — as the rightmost columns of a wide table it
                        sat beyond the horizontal scroll and the office never saw it. */}
                    <td className={`body-sm-regular ${styles.fundCell}`}>
                      <span className={styles.fundLine}>
                        {row.fundId} {row.fundName ? `— ${row.fundName}` : ''}
                      </span>
                      <span className={styles.methodLine}>
                        <span className={methodPillClass(row.classificationMethod)}>{methodLabel(row.classificationMethod)}</span>
                      </span>
                      {methodDetail(row) && <span className={styles.methodDetail}>{methodDetail(row)}</span>}
                    </td>
                    <td className={styles.assignCol}>
                      {correctingId === row.id ? (
                        <FundPicker
                          funds={funds}
                          disabled={busyId === row.id}
                          confirmLabel={t('nedarimTransactionsFundAllocation.tabs.saveCorrection')}
                          onConfirm={(fund) => correct(row, fund)}
                        />
                      ) : (
                        <Button variant="outline" onClick={() => setCorrectingId(row.id)}>
                          {t('nedarimTransactionsFundAllocation.tabs.changeFund')}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <TablePager page={safePage} pageCount={pageCount} onChange={setPage} />
    </div>
  );
};

export default AutoClassifiedTab;
